(() => {
  const state = {
    units: 0,
    appliedPercent: 0,
    displayPercent: 0,
    savings: 0,
    currency: Shopify.currency.active,
    sectionId: "",
    productIds: new Set(),
  };

  const expectedPercent = (units, tierTwo, tierThree) => {
    if (units >= 3) return Number(tierThree || 25);
    if (units >= 2) return Number(tierTwo || 15);
    return 0;
  };
  const subscribers = new Set();
  let reconciliationTimer;
  let pendingToast;
  let toastTimer;

  const formatter = () =>
    new Intl.NumberFormat(Shopify.locale, {
      style: "currency",
      currency: state.currency || Shopify.currency.active,
    });

  const notify = () => subscribers.forEach((subscriber) => subscriber(state));

  const readCarrier = (carrier) => {
    if (!carrier) return false;
    state.units = Number(carrier.dataset.units || 0);
    state.appliedPercent = Number(carrier.dataset.appliedPct || 0);
    // "0" is a real attribute value, so it must not fall through the || chain.
    // Two or more eligible clubs always unlock a tier, even before Shopify
    // writes the automatic discount onto the cart.
    const reportedDisplay = carrier.dataset.displayPct;
    state.displayPercent = reportedDisplay === undefined || reportedDisplay === ""
      ? expectedPercent(state.units)
      : Number(reportedDisplay);
    if (state.displayPercent === 0 && state.units >= 2) {
      state.displayPercent = Math.max(state.appliedPercent, expectedPercent(state.units));
    }
    state.savings = Number(carrier.dataset.savings || 0);
    state.currency = carrier.dataset.currency || Shopify.currency.active;
    state.sectionId = carrier.dataset.sectionId || state.sectionId;
    state.productIds = new Set(
      (carrier.dataset.productIds || "").split(",").filter(Boolean),
    );
    notify();
    return true;
  };

  const readHtml = (html) => {
    if (!html) return false;
    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    return readCarrier(documentFragment.querySelector("ly-mcs-state"));
  };

  const subscribe = (callback) => {
    subscribers.add(callback);
    callback(state);
    return () => subscribers.delete(callback);
  };

  const scheduleReconciliation = () => {
    window.clearTimeout(reconciliationTimer);
    reconciliationTimer = window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }, 600);
  };

  document.addEventListener("cart:change", (event) => {
    const sections = event.detail?.cart?.sections;
    if (sections && state.sectionId && sections[state.sectionId]) {
      readHtml(sections[state.sectionId]);
    }
    scheduleReconciliation();
  });

  document.addEventListener("cart-drawer:refreshed", () => {
    readCarrier(document.querySelector("#cart-drawer ly-mcs-state"));
    if (pendingToast && window.themeVariables.settings.cartType === "drawer") {
      const drawer = document.querySelector("#cart-drawer");
      const headerSlot = drawer?.querySelector('[slot="header"]');
      if (headerSlot) {
        drawer.querySelector(".ly-mcs-toast")?.remove();
        const toast = document.createElement("div");
        toast.className = "ly-mcs-toast";
        toast.setAttribute("role", "status");
        toast.setAttribute("aria-live", "polite");
        toast.innerHTML = `<span class="ly-mcs-toast__check" aria-hidden="true">✓︎</span><span></span>`;
        toast.lastElementChild.textContent = pendingToast;
        headerSlot.insertAdjacentElement("afterend", toast);
        window.clearTimeout(toastTimer);
        toastTimer = window.setTimeout(() => toast.remove(), 3500);
      }
      pendingToast = null;
    }
  });

  document.addEventListener("variant:add", (event) => {
    const items = event.detail?.items || [];
    const item = items[0];
    const title = item?.product_title || item?.title;
    const template = document.querySelector("#cart-drawer")?.dataset.mcsAddedTemplate;
    if (title && template) pendingToast = template.replace("__PRODUCT__", title);
  });

  document.addEventListener("cart:error", () => {
    pendingToast = null;
  });

  class SavingsElement extends HTMLElement {
    connectedCallback() {
      this.unsubscribe = subscribe((nextState) => this.update(nextState));
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }
  }

  class MultiClubPrice extends SavingsElement {
    update(nextState) {
      const standard = this.querySelector(".ly-mcs-price__standard");
      const preview = this.querySelector(".ly-mcs-price__preview");
      const basePrice = Number(this.dataset.price || 0);
      const percent = nextState.displayPercent;
      const active = percent > 0;
      standard.hidden = active;
      preview.hidden = !active;
      if (!active) return;

      const price = Math.round(basePrice * (100 - percent) / 100);
      this.querySelector(".ly-mcs-price__now").textContent = formatter().format(price / 100);
      this.querySelector(".ly-mcs-price__was").textContent = formatter().format(basePrice / 100);
      this.querySelector(".ly-mcs-price__badge").textContent =
        this.dataset.badgeTemplate.replace("__PERCENT__", percent);
    }
  }

  class MultiClubModule extends SavingsElement {
    update(nextState) {
      // Applied/pending copy must reflect what Shopify has actually allocated,
      // not the unit-expected display percent (which is used for price previews).
      const percent = nextState.appliedPercent;
      this.dataset.units = nextState.units;
      this.dataset.appliedPct = percent;
      const key = nextState.units === 0
        ? "0"
        : nextState.units === 1
          ? "1"
          : `${nextState.units >= 3 ? 3 : 2}-${percent > 0 ? "applied" : "pending"}`;
      this.querySelectorAll("[data-mcs-copy]").forEach((copy) => {
        copy.hidden = copy.dataset.mcsCopy !== key;
        if (!copy.hidden && copy.dataset.template) {
          copy.textContent = copy.dataset.template
            .replace("__APPLIED__", percent)
            .replace("__COUNT__", String(nextState.units));
        }
      });
      this.querySelectorAll(".ly-mcs-dots span").forEach((dot, index) => {
        dot.classList.toggle("is-filled", index < Math.min(nextState.units, 3));
      });
      const currentIndex = percent >= Number(this.dataset.tierThree)
        ? 2
        : percent >= Number(this.dataset.tierTwo)
          ? 1
          : nextState.units > 0
            ? 0
            : -1;
      this.querySelectorAll(".ly-mcs-module__tier").forEach((tier, index) => {
        tier.classList.toggle("is-current", index === currentIndex);
        tier.classList.toggle("is-passed", index < currentIndex);
      });
      const status = this.querySelector("[data-mcs-status]");
      if (status?.dataset.template) {
        status.textContent = status.dataset.template
          .replace("__COUNT__", String(nextState.units))
          .replace("__APPLIED__", String(percent));
      }
    }
  }

  class MultiClubCta extends SavingsElement {
    update(nextState) {
      const button = this.closest(".lypdp__cta-row")?.querySelector('button[type="submit"]');
      const subline = this.querySelector("[data-mcs-cta-copy]");
      if (!button || !subline) return;

      // The button price previews the unlocked tier (by design), but the
      // "currently applied" subline may only claim allocations Shopify confirmed.
      const previewPercent = nextState.displayPercent;
      const appliedPercent = nextState.appliedPercent;
      const basePrice = Number(this.dataset.price || 0);
      if (this.dataset.customize === "true") {
        const price = Math.round(basePrice * (100 - previewPercent) / 100);
        button.textContent = `${this.dataset.addLabel} — ${formatter().format(price / 100)}${previewPercent > 0 ? " ✦︎" : ""}`;
      }

      let key = "default";
      if (appliedPercent > 0) key = "applied";
      else if (nextState.units === 1) key = "unlock-two";
      else if (nextState.units === 2) key = "unlock-three";
      this.querySelectorAll("[data-mcs-cta-copy]").forEach((copy) => {
        copy.hidden = copy.dataset.mcsCtaCopy !== key;
        if (!copy.hidden && copy.dataset.template) {
          copy.textContent = copy.dataset.template.replace("__PERCENT__", appliedPercent);
        }
      });
    }
  }

  class MultiClubRecommendations extends SavingsElement {
    update(nextState) {
      const landedPercent = nextState.units === 1
        ? Number(this.dataset.tierTwo)
        : nextState.units >= 2
          ? Number(this.dataset.tierThree)
          : 0;
      const subtitleKey = nextState.units === 0
        ? "subZero"
        : nextState.units === 1
          ? "subOne"
          : nextState.units === 2
            ? "subTwo"
            : "subThree";
      const subtitle = this.querySelector("[data-mcs-recs-subtitle]");
      if (subtitle) subtitle.textContent = this.dataset[subtitleKey];

      this.querySelectorAll(".ly-mcs-rec-row, .ly-mcs-rec-card, .ly-mcs-drawer-rec").forEach((card) => {
        card.hidden = nextState.productIds.has(card.dataset.productId);
        const basePrice = Number(card.dataset.basePrice);
        const price = Math.round(basePrice * (100 - landedPercent) / 100);
        const priceLabel = card.querySelector("[data-mcs-rec-price]");
        if (!priceLabel) return;
        if (landedPercent > 0) {
          priceLabel.textContent = this.dataset.priceDiscountedTemplate
            .replace("__PRICE__", formatter().format(price / 100))
            .replace("__PERCENT__", landedPercent)
            .replace("__ORIGINAL__", formatter().format(basePrice / 100));
        } else {
          priceLabel.textContent = this.dataset.priceFullTemplate
            .replace("__PRICE__", formatter().format(basePrice / 100));
        }
      });
    }
  }

  class SavingsMeter extends SavingsElement {
    update(nextState) {
      // Meter % and tier copy follow displayPercent (unit-expected unlocked tier),
      // matching PDP price preview. Checkout totals stay on real cart money.
      const percent = nextState.displayPercent;
      const pct = this.querySelector(".ly-mcs-meter__pct");
      if (pct) {
        pct.textContent = percent > 0 ? `${percent}% off` : "0%";
      }
      this.classList.toggle("is-max", percent >= Number(this.dataset.tierThree || 25));
      this.querySelectorAll(".ly-mcs-meter__segment").forEach((segment, index) => {
        segment.classList.toggle("is-filled", index < Math.min(nextState.units, 3));
      });
      const units = nextState.units;
      this.querySelectorAll("[data-mcs-copy]").forEach((copy) => {
        const key = copy.dataset.mcsCopy;
        let show = false;
        if (key === "0") show = units === 0;
        else if (key === "1") show = units === 1;
        else if (key === "pending") show = units >= 2 && percent === 0;
        else if (key === "2-applied") show = units === 2 && percent > 0;
        else if (key === "3-applied") show = units >= 3 && percent > 0;
        copy.hidden = !show;
        if (!copy.hidden && copy.dataset.template) {
          copy.textContent = copy.dataset.template
            .replace("__APPLIED__", String(percent))
            .replace("__SAVINGS__", formatter().format((nextState.savings || 0) / 100));
        }
      });
      const stamp = this.querySelector(".ly-mcs-meter__stamp");
      if (stamp) stamp.hidden = percent < Number(this.dataset.tierThree || 25);
    }
  }

  customElements.define("ly-mcs-price", MultiClubPrice);
  customElements.define("ly-mcs-module", MultiClubModule);
  customElements.define("ly-mcs-cta", MultiClubCta);
  customElements.define("ly-mcs-recommendations", MultiClubRecommendations);
  customElements.define("ly-savings-meter", SavingsMeter);

  const initialize = () => readCarrier(document.querySelector("ly-mcs-state"));
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
